/**
 * Bulk Action Modals - Confirmation dialogs for bulk operations
 * 
 * Features:
 * - Delete confirmation with business rule warnings
 * - Status update confirmation
 * - Category/type assignment
 * - Generic bulk action modal
 */
import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { AlertTriangle, Trash2, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

// ===================================================
// DELETE CONFIRMATION MODAL
// ===================================================

export const DeleteConfirmationModal = ({
  open,
  onOpenChange,
  onConfirm,
  count = 1,
  itemType = 'item',
  loading = false,
  warnings = [],
  blockedItems = [],
  requireReason = false,
  className
}) => {
  const [reason, setReason] = useState('');
  
  const hasBlockedItems = blockedItems.length > 0;
  const deletableCount = count - blockedItems.length;
  
  const handleConfirm = () => {
    onConfirm?.(reason);
    setReason('');
  };
  
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={className}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Delete {count} {itemType}{count !== 1 ? 's' : ''}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                This action will soft-delete the selected {itemType}{count !== 1 ? 's' : ''}.
                The data can be recovered by an administrator if needed.
              </p>
              
              {/* Warnings */}
              {warnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium mb-1">Warning:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {warnings.map((warning, i) => (
                          <li key={i}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Blocked items */}
              {hasBlockedItems && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                    <div className="text-sm text-red-800">
                      <p className="font-medium mb-1">
                        {blockedItems.length} {itemType}{blockedItems.length !== 1 ? 's' : ''} cannot be deleted:
                      </p>
                      <ul className="list-disc list-inside space-y-1 max-h-24 overflow-y-auto">
                        {blockedItems.map((item, i) => (
                          <li key={i}>{item.reason || item.id}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Reason input */}
              {requireReason && (
                <div>
                  <label className="block text-sm font-medium text-ivory-700 mb-1">
                    Reason for deletion *
                  </label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter reason for deletion..."
                    rows={2}
                  />
                </div>
              )}
              
              {deletableCount > 0 && (
                <p className="text-sm text-ivory-600">
                  {hasBlockedItems 
                    ? `${deletableCount} of ${count} ${itemType}${count !== 1 ? 's' : ''} will be deleted.`
                    : `This will delete ${count} ${itemType}${count !== 1 ? 's' : ''}.`
                  }
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={loading || deletableCount === 0 || (requireReason && !reason.trim())}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              `Delete ${deletableCount > 0 ? deletableCount : ''} ${itemType}${deletableCount !== 1 ? 's' : ''}`
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// ===================================================
// BULK STATUS UPDATE MODAL
// ===================================================

export const BulkStatusUpdateModal = ({
  open,
  onOpenChange,
  onConfirm,
  count = 1,
  itemType = 'item',
  statusOptions = [],
  loading = false,
  className
}) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [note, setNote] = useState('');
  
  const handleConfirm = () => {
    onConfirm?.(selectedStatus, note);
    setSelectedStatus('');
    setNote('');
  };
  
  const handleClose = () => {
    setSelectedStatus('');
    setNote('');
    onOpenChange?.(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={className}>
        <DialogHeader>
          <DialogTitle>
            Update Status for {count} {itemType}{count !== 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>
            Select a new status to apply to all selected {itemType}s.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-ivory-700 mb-1">
              New Status *
            </label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select status..." />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-ivory-700 mb-1">
              Note (optional)
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note about this status change..."
              rows={2}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={loading || !selectedStatus}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Status'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===================================================
// BULK ASSIGN MODAL
// ===================================================

export const BulkAssignModal = ({
  open,
  onOpenChange,
  onConfirm,
  count = 1,
  itemType = 'item',
  assignType = 'category',
  options = [],
  loading = false,
  className
}) => {
  const [selectedValue, setSelectedValue] = useState('');
  
  const handleConfirm = () => {
    onConfirm?.(selectedValue);
    setSelectedValue('');
  };
  
  const handleClose = () => {
    setSelectedValue('');
    onOpenChange?.(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={className}>
        <DialogHeader>
          <DialogTitle>
            Assign {assignType} to {count} {itemType}{count !== 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>
            Select a {assignType} to assign to all selected {itemType}s.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <label className="block text-sm font-medium text-ivory-700 mb-1">
            {assignType.charAt(0).toUpperCase() + assignType.slice(1)} *
          </label>
          <Select value={selectedValue} onValueChange={setSelectedValue}>
            <SelectTrigger>
              <SelectValue placeholder={`Select ${assignType}...`} />
            </SelectTrigger>
            <SelectContent>
              {options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={loading || !selectedValue}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              'Assign'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===================================================
// GENERIC CONFIRMATION MODAL
// ===================================================

export const BulkActionConfirmModal = ({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  count = 1,
  itemType = 'item',
  variant = 'default', // 'default', 'danger'
  loading = false,
  className
}) => {
  const isDanger = variant === 'danger';
  
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={className}>
        <AlertDialogHeader>
          <AlertDialogTitle className={cn(isDanger && 'text-red-600')}>
            {title || `Confirm action for ${count} ${itemType}${count !== 1 ? 's' : ''}`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {description || `This action will affect ${count} ${itemType}${count !== 1 ? 's' : ''}.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirm?.()}
            disabled={loading}
            className={cn(isDanger && 'bg-red-600 hover:bg-red-700 focus:ring-red-600')}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              confirmLabel
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteConfirmationModal;
